
import { Component, Vue, Watch } from "vue-property-decorator";
import Scan from "@/components/Scan.vue";
import TagList from "@/components/shared/TagList.vue";
import BatchRefInput from "@/components/BatchRefInput.vue";
import BatchQuantityInput from "@/components/BatchQuantityInput.vue";
import KraalSelectInput from "@/components/KraalSelectInput.vue";
import NotesInput from "@/components/NotesInput.vue";
import AilmentSelectInput from "@/components/AilmentSelectInput.vue";
import AverageMassInput from "@/components/AverageMassInput.vue";
import TreatmentSelectInput from "@/components/TreatmentSelectInput.vue";
import TotalTagsScanned from "@/components/TotalTagsScanned.vue";
import { withdrawalDaysFromToday } from "@/helpers/withdrawal";
import { toKraalInfoBullet } from "@/helpers/ui-info-text";
import * as Models from "@gigalot/data-models";
import lodash from "lodash";

@Component({
  components: {
    Scan,
    TagList,
    BatchRefInput,
    BatchQuantityInput,
    KraalSelectInput,
    NotesInput,
    AilmentSelectInput,
    AverageMassInput,
    TreatmentSelectInput,
    TotalTagsScanned,
  },
})
export default class GroupTreatment extends Vue {
  toKraalInfoBullet = toKraalInfoBullet;

  batchNumber: string = "";
  batchQuantity: string = "";
  kraalId: string = "";
  notes: string = "";
  ailments: Models.Ailment[] = [];
  selectedAilments: Models.Ailment[] = [];
  treatments: Models.Treatment[] = [];
  selectedTreatment: Models.Treatment | null = null;
  useAverageMass: boolean = false;
  averageMass: string = "";
  appliedDoses: Models.AppliedDose[] = [];
  kraalIds: string[] = [];
  isUiLocked: boolean = false;

  get shownTreatments(): Models.Treatment[] {
    if (this.useAverageMass) {
      // return treatments that have at least one cc-mass-dedendent dose
      return this.treatments.filter((t) => (t.doses ?? []).find((d) => d.type === "cc-mass-dependent"));
    } else {
      // not using mass, only return treatments with doses that don't depend on mass for dosage
      return this.treatments.filter((t) => !(t.doses ?? []).find((d) => d.type === "cc-mass-dependent"));
    }
  }

  get selectedAilmentLabels(): string {
    if (!this.selectedAilments.length) return "";
    const labels = this.selectedAilments.map((a) => a.ailment).filter((a): a is string => !!a);
    return labels.length ? labels.join(", ") : `${this.selectedAilments.length} selected`;
  }

  isAverageMassNumber(): boolean {
    const value = Number(this.averageMass);
    return Number.isFinite(value);
  }

  isAverageMassPositive(): boolean {
    const value = Number(this.averageMass);
    return Number.isFinite(value) && value > 0;
  }

  isAverageMassValid(): boolean {
    return this.isAverageMassPositive();
  }

  getAverageMass(): number | undefined {
    const value = Number(this.averageMass);
    if (!Number.isFinite(value) || value <= 0) return undefined;
    return value;
  }

  async created() {
    this.$store.commit("navFuncs", {
      save: undefined,
      back: () => {
        this.$router.go(-1);
      },
    });

    this.ailments = await this.$store.dispatch("data/getAilments");
    this.treatments = await this.$store.dispatch("data/getTreatments");
  }

  onClearWhenSaved() {
    this.batchNumber = "";
    this.batchQuantity = "";
    this.kraalId = "";
    this.notes = "";
    this.selectedAilments = [];
    this.selectedTreatment = null;
    this.useAverageMass = false;
    this.averageMass = "";
    this.$store.commit("groupTreatment/batchQuantity", "");
    this.$store.commit("groupTreatment/batchNumber", "");
    this.$store.commit("groupTreatment/kraalId", "");
    this.$store.commit("groupTreatment/notes", "");
    this.$store.commit("groupTreatment/ailments", []);
    this.$store.commit("groupTreatment/averageMass", undefined);
  }

  sgtins: string[] = [];
  animals: (Models.Animal & { withdrawalDays?: number })[] = [];

  get tags() {
    const tags: Models.Tag[] = this.$store.state.scan.tags;
    this.sgtins = tags.map((t) => t.sgtin);
    return this.$store.state.scan.tags;
  }

  @Watch("sgtins")
  onSgtinsChanged(val: string[], oldVal: string[]) {
    const newSgtins = lodash.difference(val, oldVal); //only lookup new sgtins, otherwise it seems that the system looks up too many of the same animals
    const addAnimal = async (sgtin: string) => {
      const animal = await this.$store.dispatch("data/getAnimal", sgtin);
      if (animal) {
        animal.withdrawalDays = withdrawalDaysFromToday(animal);
        this.animals.push(animal);
      }
    };
    for (const sgtin of newSgtins) addAnimal(sgtin);

    //filter out any animals with sgtin no longer being used
    this.animals = this.animals.filter((a) => this.sgtins.find((sgtin) => a.sgtin === sgtin) !== undefined);
  }

  @Watch("batchNumber")
  async onBatchNumberChanged(val: any, oldVal: any) {
    this.$store.commit("groupTreatment/batchNumber", val);
  }

  @Watch("batchQuantity")
  async onBatchQuantityChanged(val: any, oldVal: any) {
    this.$store.commit("groupTreatment/batchQuantity", parseInt(val));
  }

  @Watch("kraalId")
  async onKraalIdChanged(val: any, oldVal: any) {
    this.$store.commit("groupTreatment/kraalId", val);
  }

  @Watch("notes")
  async onNotesChanged(val: any, oldVal: any) {
    this.$store.commit("groupTreatment/notes", val);
  }

  @Watch("selectedAilments")
  onSelectedAilmentsChanged(val: Models.Ailment[]) {
    this.$store.commit("groupTreatment/ailments", val);
  }

  @Watch("useAverageMass")
  onUseAverageMassChanged(val: any) {
    //this.setAppliedTreatment;
    this.selectedTreatment = null;
  }

  @Watch("averageMass")
  onAverageMassChanged(val: any) {
    this.$store.commit("groupTreatment/averageMass", this.getAverageMass());
    this.determineAppliedTreatment();
  }

  @Watch("selectedTreatment")
  onSelectedTreatmentChanged(treatment?: Models.Treatment) {
    console.log("onSelectedTreatmentChanged: " + JSON.stringify(treatment));
    this.determineAppliedTreatment();
  }

  async mounted() {
    window.scrollTo({ top: 0 });
    this.$store.commit("scan/setAppMode", "group-treatment");

    if (this.$store.state.scan.appState === "saving") {
      this.$store.commit("scan/flagError");
    }
    this.$store.dispatch("power/setPowerMode", "high");

    this.kraalIds = await this.$store.dispatch("data/getKraalIds");

    //await this.$store.dispatch("data/linkSgtinsToAnimalsFromDb", this.tags);

    if (this.tags.length > 0) {
      this.batchQuantity = this.$store.state.groupTreatment.batchQuantity;
      this.batchNumber = this.$store.state.groupTreatment.batchNumber;
      this.notes = this.$store.state.groupTreatment.notes;
      this.selectedAilments = this.$store.state.groupTreatment.ailments || [];
      this.kraalId = this.$store.state.groupTreatment.kraalId || "";
      const savedAverageMass = this.$store.state.groupTreatment.averageMass;
      this.averageMass = savedAverageMass !== undefined ? `${savedAverageMass}` : "";
      this.useAverageMass = savedAverageMass !== undefined;
    } else {
      this.$store.commit("groupTreatment/batchQuantity", "");
      this.$store.commit("groupTreatment/batchNumber", "");
      this.$store.commit("groupTreatment/kraalId", "");
      this.$store.commit("groupTreatment/notes", "");
      this.$store.commit("groupTreatment/ailments", []);
      this.$store.commit("groupTreatment/averageMass", undefined);
      this.averageMass = "";
      this.useAverageMass = false;
    }

    this.setAppliedTreatment(null);
  }

  async onSave() {
    await this.$store.dispatch("groupTreatment/save");
    this.isUiLocked = true;
  }

  setAppliedTreatment(appliedTreatment: Models.AppliedTreatment | null | undefined) {
    if (appliedTreatment === null) {
      appliedTreatment = undefined;
    }
    this.$store.commit("groupTreatment/appliedTreatment", appliedTreatment);
    this.appliedDoses = appliedTreatment?.appliedDoses ?? [];
  }

  determineAppliedTreatment() {
    if (!this.selectedTreatment) {
      this.setAppliedTreatment(undefined);
      return;
    }

    try {
      const appliedTreatment = new Models.AppliedTreatment();
      appliedTreatment.treatment = this.selectedTreatment;
      appliedTreatment.appliedDoses = [];

      const isMassValid = this.isAverageMassValid();
      const mass = this.getAverageMass();

      for (const dose of this.selectedTreatment.doses) {
        const appliedDose = new Models.AppliedDose();
        if (!dose.item) throw Error(`${this.selectedTreatment.description} has a dose that has no dispensary item`);
        appliedDose.item = dose.item;
        appliedDose.unit = dose.unit;
        if (!dose.amount) {
          throw Error(`${this.selectedTreatment.description} has a dose that does not have amount set`);
        }
        if (dose.type === "cc-mass-dependent") {
          if (!dose.kgPerDose) throw Error(`${this.selectedTreatment.description} has a dose that is cc-mass-dependent but does not have kgPerDose set`);
          if (!isMassValid) throw Error(`${this.selectedTreatment.description} required a valid mass`);
          //Math.ceil might not work for other feedlots. So far this is good for Sernick. We might need this to be configurable
          appliedDose.actualDose = Math.ceil(((mass as number) / dose.kgPerDose) * dose.amount);
        } else {
          appliedDose.actualDose = dose.amount;
        }
        appliedTreatment.appliedDoses.push(appliedDose);
      }
      this.setAppliedTreatment(appliedTreatment);
    } catch (err) {
      this.setAppliedTreatment(undefined);
      console.error(err);
    }
  }
}
